import React from 'react';
import _ from 'lodash';

import clsx from 'classnames';
import { useQueryClient } from '@tanstack/react-query';
import { makeStyles } from '@mui/styles';
import CbButton from '../../../../../components/Buttons/CbButton';
import { QuestionCard as Card } from '../../../../renewals/agency/P100/RenewalComponents';
import { Modal } from '../../../../../components/modals/v2/helpers/v2.modal.helpers';
import { InvalidateQuotesConfig } from '../../../../../components/modals/InvalidateQuotes';
import { withShowable } from '../../../../_global/lib/withShowable';

// FORMS
import { AccountDetailsForm } from '../../../../../components/accounts/AccountDetailsForm';
import { AccountOrgContactForm } from '../../../../../components/accounts/AccountOrganizationContactForm';
import AccountP100SecurityQuestionsForm from '../../../../../components/accounts/AccountP100SecurityQuestionsForm';
// CHILDREN FIELDS
import { AccountOrgContactRenewalFields } from '../../../../../components/accounts/fields/AccountOrgContactRenewalFields';
import { P100RenewalSecurityQuestionsFields } from '../../../../../components/accounts/fields/P100RenewalSecurityQuestionsFields';
import { AccountDetailsRenewalFields } from '../../../../../components/accounts/fields/AccountDetailsRenewalFields';
// Services
import {
  updateOrgProfile,
  updateSecurityQuestions,
  verifyOrgProfile,
} from '../../../../../accounts/AccountService';
import {
  standaridizeQuoteDetails,
  standaridizeUndefinedDetails,
  removeAttestCheck,
} from '../../../../renewals/renewal.utils';
import { actionOnCustomer } from '../../../../../inbox/QuotesService';
import { manageAPIError } from '../../../../../utils/error.utils';
import CustomerRenewalAttestSubmit from './CustomerRenewalAttestSubmit';
import { ProductTypes } from '../../../../../types';
import useNotifications from '../../../../../_lib/notifications/useNotifications';

const QuestionCard = withShowable(Card);

const CustomerPrime100AccountProfileWithoutRenewalChanges = ({
  quote,
  onMovingAhead,
}: any) => {
  const { formattedQuote, rawQuote } = quote;
  const queryClient = useQueryClient();
  const classes = styles();
  const notifications = useNotifications();

  const standaridizeQuoteData = React.useMemo(
    () => standaridizeQuoteDetails(rawQuote),
    [rawQuote]
  );

  const orgContactDefaultValues = React.useMemo(
    () => defaultOrganizationValues(standaridizeQuoteData),
    [standaridizeQuoteData]
  );

  const missingQuestions = React.useMemo(
    () => standaridizeUndefinedDetails(standaridizeQuoteData),
    [standaridizeQuoteData]
  );

  function handleAccountDetails(formData: any) {
    return handleVerification(
      quote.rawQuote,
      {
        accountId: quote.rawQuote.accountId,
        ...standaridizeQuoteData,
        ...formData,
      },
      'firmo',
      notifications
    ).then((response: any) => {
      if (response) {
        return updateOrgProfile({}, response)
          .then(() => {
            removeAttestCheck();
            queryClient.invalidateQueries(['renewal-quote']);
            notifications.success('Quote Details Updated Successfully.');
          })
          .catch((error: any) => {
            const errorResponse = manageAPIError(error);
            notifications.error(errorResponse);
            return false;
          });
      }
    });
  }

  function handleOrgContact(formData: any) {
    return actionOnCustomer(
      {},
      {
        quoteId: quote.rawQuote.id,
        accountName: _.get(quote.rawQuote, 'firmographicData.name'),
        agentEmail: _.get(quote.rawQuote, 'agentEmail'),
        agentFirstName: _.get(quote.rawQuote, 'agentFirstName'),
        agentLastName: _.get(quote.rawQuote, 'agentLastName'),
        agentPhone: _.get(quote.rawQuote, 'agentPhone'),
        customerFirstName: _.get(formData, 'contactFirstName'),
        customerLastName: _.get(formData, 'contactLastName'),
        customerPhone: _.get(formData, 'contactPhone'),
        customerEmail: _.get(formData, 'contactEmail'),
      }
    )
      .then(() => {
        removeAttestCheck();
        queryClient.invalidateQueries(['renewal-quote']);
        notifications.success(
          'Organization Contact has been updated successfully.'
        );
      })
      .catch((error: any) => {
        const errorResponse = manageAPIError(error);
        notifications.error(errorResponse);
      });
  }

  function handleSecurityQuestions(formData: any) {
    // @ts-expect-error TS(2554): Expected 4 arguments, but got 3.
    handleVerification(
      quote.rawQuote,
      {
        accountId: quote.rawQuote.accountId,
        ...standaridizeQuoteData,
        ...formData,
      },
      notifications
    ).then((response: any) => {
      if (response) {
        return updateSecurityQuestions({ isRenewal: true }, { ...response })
          .then(() => {
            removeAttestCheck();
            queryClient.invalidateQueries(['renewal-quote']);
            notifications.success('Security Assessment Updated Successfully.');
          })
          .catch((error: any) => {
            const errorResponse = manageAPIError(error);
            notifications.error(errorResponse);
          });
      }
    });
  }

  return (
    <div>
        <AccountDetailsForm
          defaultValues={standaridizeQuoteData}
          handleOnSubmit={handleAccountDetails}
        >
          {() => {
            return (
              <QuestionCard>
                  <div className="flex--spaced">
                    <h3 className={clsx('contrast-text', classes.miniheading)}>
                      Account Information
                    </h3>
                  </div>
                  <AccountDetailsRenewalFields
                    showableQuestions={_.get(
                      missingQuestions,
                      'undefinedAccountDetails'
                    )}
                    quote={standaridizeQuoteData}
                  />
                  <div className={classes.buttonLayout}>
                    <CbButton
                      form="accountDetails"
                      styleName="ctaButton"
                      buttonText="save"
                      type="submit"
                    />
                  </div>
                </QuestionCard>
            );
          }}
        </AccountDetailsForm>

        <AccountOrgContactForm
          formName=""
          defaultValues={orgContactDefaultValues}
          handleOnSubmit={handleOrgContact}
        >
          {() => {
            return (
              <QuestionCard
                  show={missingQuestions.undefinedPolicyContact.length > 0}
                >
                  <div className="flex--spaced">
                    <h3 className={clsx('contrast-text', classes.miniheading)}>
                      Organization Contact
                    </h3>
                  </div>
                  <AccountOrgContactRenewalFields
                    showableQuestions={handleMissingContactStandarization(
                      _.get(missingQuestions, 'undefinedPolicyContact', [])
                    )}
                  />
                  <div className={classes.buttonLayout}>
                    <CbButton
                      form="orgContact"
                      styleName="ctaButton"
                      buttonText="save"
                      type="submit"
                    />
                  </div>
                </QuestionCard>
            );
          }}
        </AccountOrgContactForm>

        <AccountP100SecurityQuestionsForm
          formName=""
          handleOnSubmit={handleSecurityQuestions}
          defaultValues={standaridizeQuoteData}
        >
          {() => {
            return (
              <QuestionCard
                  show={missingQuestions.undefinedSecurityQuestions.length > 0}
                >
                  <div className="flex--spaced">
                    <h3 className={clsx('contrast-text', classes.miniheading)}>
                      Security Questions
                    </h3>
                  </div>
                  <P100RenewalSecurityQuestionsFields
                    showableQuestions={_.get(
                      missingQuestions,
                      'undefinedSecurityQuestions'
                    )}
                  />
                  <div className={classes.buttonLayout}>
                    <CbButton
                      form="P100SecQuestions"
                      styleName="ctaButton"
                      buttonText="save"
                      type="submit"
                    />
                  </div>
                </QuestionCard>
            );
          }}
        </AccountP100SecurityQuestionsForm>
        <CustomerRenewalAttestSubmit
          data={{ ...formattedQuote, product: ProductTypes.p100 }}
          onMovingAhead={onMovingAhead}
        />
      </div>
  );
};

function handleVerification(
  quote: any,
  payload: any,
  update: any,
  notifications: any
) {
  return verifyOrgProfile({ renewal: true }, { ...payload })
    .then((resp: any) => {
      const { WillInvalidateQuotes } = resp.data;
      if (WillInvalidateQuotes) {
        Modal.show(InvalidateQuotesConfig, {
          data: { update, data: { ...payload } },
          config: {
            title: `Account Changes for ${_.get(
              quote,
              'firmographicData.name',
              'Renewals'
            )}`,
          },
        });
      } else {
        return payload;
      }
    })
    .catch((error: any) => {
      const errorResponse = manageAPIError(error);
      notifications.error(errorResponse);
    });
}

function defaultOrganizationValues(standardizeQuoteData: any) {
  return {
    contactFirstName: _.get(standardizeQuoteData, 'policyContactFirstName'),
    contactLastName: _.get(standardizeQuoteData, 'policyContactLastName'),
    contactPhone: _.get(standardizeQuoteData, 'policyContactPhone'),
    contactEmail: _.get(standardizeQuoteData, 'policyContactEmail'),
  };
}

function handleMissingContactStandarization(standarizedQuestions: any) {
  return standarizedQuestions.map((missingQuestions: any) => {
    return `c${missingQuestions.substring(7)}`;
  });
}

const styles = makeStyles(({ config, palette }) => ({
  additionalInsuredCard: {
    padding: '2.08rem',
  },
  buttonLayout: {
    display: 'flex',
    justifyContent: 'center',
    paddingTop: '2.85rem',
    paddingBottom: '2.08rem',
  },
  miniheading: {
    fontWeight: config.weights.bold,
    fontSize: '1.66rem',
    paddingLeft: '2.08rem',
    marginBottom: '0rem',
  },
}));

export default CustomerPrime100AccountProfileWithoutRenewalChanges;
